import { useRef } from "react";
import { X, Download } from "lucide-react";

export function PageHeader({ title, subtitle, children }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
      <div>
        <h1 className="font-heading text-2xl lg:text-3xl font-extrabold text-slate-900">{title}</h1>
        {subtitle && <p className="text-slate-500 text-sm mt-1">{subtitle}</p>}
      </div>
      <div className="flex flex-wrap items-center gap-2 max-w-full">{children}</div>
    </div>
  );
}

export function MonthSelector({ value, onChange }) {
  return (
    <input
      type="month"
      data-testid="month-selector"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="px-3 py-2 rounded-md border border-slate-300 text-sm focus:ring-2 focus:ring-sky-500 outline-none bg-white"
    />
  );
}

export function Card({ children, className = "", ...rest }) {
  return (
    <div className={`bg-white border border-slate-200 rounded-md ${className}`} {...rest}>{children}</div>
  );
}

export function Kpi({ label, value, accent = "text-slate-900", testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4" data-testid={testid}>
      <div className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</div>
      <div className={`mt-2 font-heading text-2xl font-extrabold tabular ${accent}`}>{value}</div>
    </div>
  );
}

export function Btn({ children, variant = "primary", className = "", ...props }) {
  const styles = {
    primary: "bg-sky-700 hover:bg-sky-800 text-white",
    secondary: "bg-white text-slate-700 border border-slate-300 hover:bg-slate-50",
    danger: "bg-white text-red-600 border border-red-200 hover:bg-red-50",
  };
  return (
    <button
      className={`px-3.5 py-2 rounded-md text-sm font-semibold transition-colors duration-150 flex items-center gap-2 disabled:opacity-60 ${styles[variant]} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}

export function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-700 mb-1">{label}</span>
      {children}
    </label>
  );
}

export const inputCls =
  "w-full px-3 py-2 rounded-md border border-slate-300 focus:ring-2 focus:ring-sky-500 focus:border-sky-500 outline-none text-sm bg-white";

export function Modal({ open, onClose, title, children, wide }) {
  const downTarget = useRef(null);
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
      onMouseDown={(e) => { downTarget.current = e.target; }}
      onClick={(e) => { if (e.target === e.currentTarget && downTarget.current === e.currentTarget) onClose(); }}
    >
      <div
        className={`bg-white rounded-lg shadow-lg w-full ${wide ? "max-w-2xl" : "max-w-md"} max-h-[90vh] overflow-y-auto`}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 sticky top-0 bg-white">
          <h3 className="font-heading font-bold text-slate-900">{title}</h3>
          <button onClick={onClose} data-testid="modal-close" className="text-slate-400 hover:text-slate-700">
            <X size={18} />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

export const thisMonth = () => new Date().toISOString().slice(0, 7);

export function ExportButton({ onClick, label = "Scarica CSV", testid = "export-csv" }) {
  return (
    <Btn variant="secondary" onClick={onClick} data-testid={testid}>
      <Download size={16} /> {label}
    </Btn>
  );
}
